import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import {
  CreateItAssetDocumentManuallyDocument,
  ItAssetDocumentsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'

interface Props {
  assetId: string
  onClose: () => void
}

const UploadAssetDocumentModal = ({ assetId, onClose }: Props) => {
  const [files, setFiles] = React.useState<File[]>([])
  const [isUploading, setIsUploading] = React.useState(false)

  const [createItAssetDocument] = useMutation(CreateItAssetDocumentManuallyDocument, {
    extractorFn: (data) => data.itManagementMutations.createManuallyItmanagementItAssetDocument,
  })

  const handleFilesAdd = (newFiles: File[]) => {
    setFiles([...files, ...newFiles])
  }

  const handleFileDelete = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (files.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.asset_details.documents.no_files_selected'),
      })
      return
    }

    setIsUploading(true)

    try {
      // Upload each file and create the IT asset document link
      for (const file of files) {
        const result = await createItAssetDocument({
          variables: {
            itAssetId: assetId,
            document: file,
          },
          refetchQueries: [
            {
              query: ItAssetDocumentsDocument,
              variables: { itAssetId: assetId },
            },
          ],
        })

        const handled = handleResult(result, () => undefined)
        if (!handled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.documents_upload_failed'),
          })
          return
        }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.documents_upload_failed'),
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <OneModal isOpen onClose={onClose}>
      <OneModal.Header title={i18n.t('it_management.asset_details.documents.upload_document')} />
      <OneModal.Content withPadding>
        <Box gap="s16" flexDirection="column">
          <Text size="200" weight="medium" color="grey1000">
            {i18n.t('it_management.asset_details.documents.upload_document')}
          </Text>
          <DropzoneBox
            onDrop={handleFilesAdd}
            message={i18n.t('documents.drop_here')}
            accept={acceptedContentTypes()}
            iconName="common-file-text-upload"
          />
          <FileList files={files} onDeleteFile={handleFileDelete} spaced={false} />
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" gap="s12" justifyContent="flexEnd" width="full">
          <F0Button
            variant="outline"
            onClick={onClose}
            label={i18n.t('common.cancel')}
            disabled={isUploading}
          />
          <F0Button
            variant="primary"
            onClick={handleSubmit}
            label={isUploading ? i18n.t('common.uploading') : i18n.t('common.upload')}
            type="submit"
            loading={isUploading}
            disabled={files.length === 0}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default UploadAssetDocumentModal
